import React, { createContext, useContext, useState, useCallback } from "react";
import { useRouter } from "expo-router";
import {
  useResidentsFilterStorage,
  ResidentsFilterForm,
} from "../hooks/useResidentsFilterStorage";

interface ResidentsFilterContextType {
  filters: ResidentsFilterForm;
  search: string;
  updateFilters: (newFilters: Partial<ResidentsFilterForm>) => void;
  updateSearch: (searchTerm: string) => void;
  clearAllFilters: () => void;
  hasFilters: boolean;
  isLoading: boolean;
}

const ResidentsFilterContext = createContext<
  ResidentsFilterContextType | undefined
>(undefined);

interface Props {
  children: React.ReactNode;
}

export function ResidentsFilterProvider({ children }: Props) {
  const router = useRouter();
  const [filters, setFilters] = useState<ResidentsFilterForm>({});
  const [search, setSearch] = useState<string>("");

  const { isLoading, saveFiltersToStorage, clearFilters } =
    useResidentsFilterStorage((loadedFilters) => {
      // When filters are loaded from storage, apply them
      setFilters(loadedFilters);
      setSearch(loadedFilters.search || "");
    });

  const updateFilters = useCallback(
    async (newFilters: Partial<ResidentsFilterForm>) => {
      const updatedFilters = { ...filters, ...newFilters };
      setFilters(updatedFilters);

      // Save to storage
      await saveFiltersToStorage(updatedFilters);
    },
    [filters, saveFiltersToStorage],
  );

  const updateSearch = useCallback(
    async (searchTerm: string) => {
      setSearch(searchTerm);
      await updateFilters({ search: searchTerm });
    },
    [updateFilters],
  );

  const clearAllFilters = useCallback(async () => {
    const emptyFilters: ResidentsFilterForm = {};
    setFilters(emptyFilters);
    setSearch("");

    await clearFilters();
  }, [clearFilters]);

  const hasFilters =
    !!filters.location_id ||
    !!filters.floor_id ||
    !!filters.status ||
    !!filters.gender ||
    !!filters.status_flag_id ||
    !!filters.admission_start_date_from ||
    !!filters.admission_start_date_to ||
    !!filters.admission_end_date_from ||
    !!filters.admission_end_date_to;

  return (
    <ResidentsFilterContext.Provider
      value={{
        filters,
        search,
        updateFilters,
        updateSearch,
        clearAllFilters,
        hasFilters,
        isLoading,
      }}
    >
      {children}
    </ResidentsFilterContext.Provider>
  );
}

export function useResidentsFilterContext() {
  const context = useContext(ResidentsFilterContext);
  if (!context) {
    throw new Error(
      "useResidentsFilterContext must be used within ResidentsFilterProvider",
    );
  }
  return context;
}
